import {
  ChevronDown,
  ChevronRight,
  ExternalLink,
  Instagram,
  Mail,
  MapPin,
  Phone,
  UserPlus,
  Users,
} from 'lucide-react'
import { useState } from 'react'
import type { NodeExecution } from 'sprites-types/api/v2/workflows'
import { getToolResultSchema } from 'sprites-types/workflow/tools/common'
import {
  instagramScraper,
  type InstagramScraperResult,
} from 'sprites-types/workflow/tools/instagramScraper'
import {
  createWorkflowToolComponent,
  type WorkflowToolComponentProps,
} from '../shared/WorkflowToolComponent'
import { SchemaValidationError } from '../shared/safeParseToolResult'

interface ProfileItemProps {
  profile: any
}

function ProfileItem({ profile }: ProfileItemProps) {
  const [isExpanded, setIsExpanded] = useState(false)

  return (
    <div className="border-b border-gray-200 last:border-b-0">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full px-4 py-3 text-left transition-colors hover:bg-gray-50"
      >
        <div className="flex items-center gap-3">
          <div className="flex-shrink-0">
            {isExpanded ? (
              <ChevronDown className="h-4 w-4 text-gray-500" />
            ) : (
              <ChevronRight className="h-4 w-4 text-gray-500" />
            )}
          </div>
          <div className="flex-shrink-0">
            {profile.pfp ? (
              <img
                src={profile.pfp}
                alt={profile.username}
                className="h-10 w-10 rounded-full object-cover"
                onError={(e) => {
                  e.currentTarget.style.display = 'none'
                }}
              />
            ) : (
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-pink-100">
                <Instagram className="h-5 w-5 text-pink-600" />
              </div>
            )}
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <span className="truncate font-semibold text-gray-900">@{profile.username}</span>
              {profile.is_verified && (
                <svg className="h-4 w-4 text-blue-500" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
                </svg>
              )}
            </div>
            <div className="truncate text-sm text-gray-600">{profile.full_name}</div>
          </div>
          <div className="flex-shrink-0 text-right">
            <div className="text-xs text-gray-500">
              {profile.followers?.toLocaleString() || 0} followers
            </div>
          </div>
        </div>
      </button>

      {isExpanded && (
        <div className="space-y-3 bg-gray-50 px-4 pb-4 pt-2">
          {/* Stats */}
          <div className="grid grid-cols-3 gap-2">
            <div className="rounded-lg bg-white py-2 text-center">
              <div className="text-sm font-semibold text-gray-900">
                {profile.followers?.toLocaleString() || 0}
              </div>
              <div className="text-xs text-gray-500">Followers</div>
            </div>
            <div className="rounded-lg bg-white py-2 text-center">
              <div className="text-sm font-semibold text-gray-900">
                {profile.following?.toLocaleString() || 0}
              </div>
              <div className="text-xs text-gray-500">Following</div>
            </div>
            <div className="rounded-lg bg-white py-2 text-center">
              <div className="text-sm font-semibold text-gray-900">
                {profile.posts?.toLocaleString() || 0}
              </div>
              <div className="text-xs text-gray-500">Posts</div>
            </div>
          </div>

          {/* Bio */}
          {profile.bio && (
            <div className="rounded-lg bg-white p-3">
              <div className="mb-1 text-xs font-medium text-gray-500">Bio</div>
              <div className="whitespace-pre-wrap text-sm text-gray-900">{profile.bio}</div>
            </div>
          )}

          {/* Contact Info */}
          {(profile.email || profile.phone || profile.website) && (
            <div className="space-y-2 rounded-lg bg-white p-3">
              <div className="mb-2 text-xs font-medium text-gray-500">Contact Information</div>
              {profile.email && (
                <div className="flex items-center gap-2 text-sm">
                  <Mail className="h-4 w-4 text-gray-400" />
                  <a href={`mailto:${profile.email}`} className="text-pink-600 hover:underline">
                    {profile.email}
                  </a>
                </div>
              )}
              {profile.phone && (
                <div className="flex items-center gap-2 text-sm">
                  <Phone className="h-4 w-4 text-gray-400" />
                  <a href={`tel:${profile.phone}`} className="text-pink-600 hover:underline">
                    {profile.phone}
                  </a>
                </div>
              )}
              {profile.website && (
                <div className="flex items-center gap-2 text-sm">
                  <ExternalLink className="h-4 w-4 text-gray-400" />
                  <a
                    href={profile.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="truncate text-pink-600 hover:underline"
                  >
                    {profile.website}
                  </a>
                </div>
              )}
            </div>
          )}

          {/* Location & Category */}
          {(profile.location || profile.category) && (
            <div className="space-y-2 rounded-lg bg-white p-3">
              {profile.location && (
                <div className="flex items-center gap-2 text-sm">
                  <MapPin className="h-4 w-4 text-gray-400" />
                  <span className="text-gray-700">{profile.location}</span>
                </div>
              )}
              {profile.category && (
                <div className="flex items-center gap-2 text-sm">
                  <div className="flex h-4 w-4 items-center justify-center">
                    <div className="h-2 w-2 rounded-full bg-gray-400" />
                  </div>
                  <span className="text-gray-700">{profile.category}</span>
                </div>
              )}
            </div>
          )}

          {/* Account Type */}
          <div className="flex gap-2">
            {profile.is_business && (
              <span className="inline-flex items-center rounded-full bg-blue-100 px-2 py-1 text-xs font-medium text-blue-700">
                Business
              </span>
            )}
            {profile.is_verified && (
              <span className="inline-flex items-center rounded-full bg-blue-100 px-2 py-1 text-xs font-medium text-blue-700">
                Verified
              </span>
            )}
            {profile.is_private && (
              <span className="inline-flex items-center rounded-full bg-gray-100 px-2 py-1 text-xs font-medium text-gray-700">
                Private
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

function InstagramScraperDisplayComponent({ nodeExecution }: WorkflowToolComponentProps) {
  const [isListCollapsed, setIsListCollapsed] = useState(false)
  const schema = getToolResultSchema(instagramScraper, nodeExecution.inputs)
  const parseResult = schema.safeParse(nodeExecution.outputs)

  if (!parseResult.success) {
    return <SchemaValidationError error={parseResult.error} nodeExecution={nodeExecution} />
  }

  const result = parseResult.data as InstagramScraperResult

  const inputs = nodeExecution.inputs.functionArgs as any
  const actionName = inputs?.action?.name || 'unknown'
  const params = inputs?.action?.params || {}

  // Get profiles array and sort: prioritize profiles with emails, then sort alphabetically by username
  const profiles =
    'profiles' in result
      ? [...result.profiles].sort((a, b) => {
          // First priority: profiles with emails come first
          const aHasEmail = !!a.email
          const bHasEmail = !!b.email

          if (aHasEmail && !bHasEmail) return -1
          if (!aHasEmail && bHasEmail) return 1

          // Second priority: sort alphabetically by username
          return a.username.localeCompare(b.username)
        })
      : []

  // Count profiles with emails
  const profilesWithEmails = profiles.filter((p: any) => p.email).length

  // Determine action type and display accordingly
  const getActionDisplay = () => {
    switch (actionName) {
      case 'scrapeProfile':
        return {
          icon: <Instagram className="h-5 w-5 text-pink-600" />,
          title: 'Instagram Profile Scraped with Email Extraction',
          subtitle: `@${params.username}`,
          count: profiles.length,
          label: 'profile',
        }
      case 'scrapeFollowers':
        return {
          icon: <Users className="h-5 w-5 text-pink-600" />,
          title: 'Followers Scraped with Email Extraction',
          subtitle: `from @${params.username}`,
          count: 'total_scraped' in result ? result.total_scraped : profiles.length,
          label: 'followers',
        }
      case 'scrapeFollowing':
        return {
          icon: <UserPlus className="h-5 w-5 text-pink-600" />,
          title: 'Following Scraped with Email Extraction',
          subtitle: `from @${params.username}`,
          count: 'total_scraped' in result ? result.total_scraped : profiles.length,
          label: 'following',
        }
      default:
        return {
          icon: <Instagram className="h-5 w-5 text-pink-600" />,
          title: 'Instagram Scraper',
          subtitle: params.username || 'unknown',
          count: profiles.length,
          label: 'profiles',
        }
    }
  }

  const actionDisplay = getActionDisplay()

  return (
    <div
      className={`flex overflow-hidden rounded-3xl border border-gray-200/50 bg-white/70 text-black shadow-sm ${
        isListCollapsed ? 'flex-col' : 'h-full flex-col'
      }`}
    >
      {/* Header */}
      <div
        className={`flex flex-shrink-0 items-start gap-4 px-4 py-3 ${
          !isListCollapsed && profiles.length > 0 ? 'border-b border-gray-200' : ''
        }`}
      >
        <div className="mt-1 flex-shrink-0">
          <div className="rounded-full bg-pink-100 p-2">{actionDisplay.icon}</div>
        </div>
        <div className="min-w-0 flex-1">
          <div className="mb-1 flex items-center gap-2">
            <Instagram className="h-4 w-4 text-pink-600" />
            <div className="text-sm font-semibold text-gray-900">{actionDisplay.title}</div>
          </div>

          <div className="space-y-1 text-xs">
            <div className="text-gray-700">
              Target: <span className="font-medium">{actionDisplay.subtitle}</span>
            </div>
            <div className="text-gray-700">
              Scraped: <span className="font-medium">{actionDisplay.count}</span>{' '}
              {actionDisplay.label}
            </div>
            <div className="text-gray-700">
              <Mail className="mr-1 inline h-3 w-3 text-pink-600" />
              Emails found: <span className="font-medium text-pink-600">{profilesWithEmails}</span>
            </div>
          </div>
        </div>
        {profiles.length > 0 && (
          <button
            onClick={() => setIsListCollapsed(!isListCollapsed)}
            className="mt-1 flex-shrink-0 rounded-lg p-2 transition-colors hover:bg-gray-100"
            aria-label={isListCollapsed ? 'Expand list' : 'Collapse list'}
          >
            {isListCollapsed ? (
              <ChevronRight className="h-5 w-5 text-gray-500" />
            ) : (
              <ChevronDown className="h-5 w-5 text-gray-500" />
            )}
          </button>
        )}
      </div>

      {/* Profile List */}
      {profiles.length > 0 && !isListCollapsed && (
        <div className="scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100 min-h-0 flex-1 overflow-y-auto">
          {profiles.map((profile: any, index: number) => (
            <ProfileItem key={profile.id || index} profile={profile} />
          ))}
        </div>
      )}

      {profiles.length === 0 && (
        <div className="px-4 py-8 text-center">
          <div className="text-gray-500">No profiles found</div>
        </div>
      )}
    </div>
  )
}

function downloadInstagramScraper(_nodeExecution: NodeExecution): Promise<void> {
  return Promise.resolve()
}

function instagramScraperToMarkdown(nodeExecution: NodeExecution): string {
  const schema = getToolResultSchema(instagramScraper, nodeExecution.inputs)
  const parseResult = schema.safeParse(nodeExecution.outputs)

  if (!parseResult.success) {
    return '## Error: Schema validation failed\n\nUnable to export results due to data structure mismatch.'
  }

  const result = parseResult.data as InstagramScraperResult
  const inputs = nodeExecution.inputs.functionArgs as any
  const actionName = inputs?.action?.name || 'unknown'
  const params = inputs?.action?.params || {}

  let markdown = '## Instagram Scraper\n\n'

  switch (actionName) {
    case 'scrapeProfile':
      markdown += `**Action:** Scrape Profile\n**Username:** @${params.username}\n`
      if (params.emailLimit) markdown += `**Email Limit:** ${params.emailLimit}\n`
      markdown += `\n`
      if ('profiles' in result && result.profiles.length > 0) {
        const profile = result.profiles[0]
        markdown += `### Profile Details\n\n`
        markdown += `- **Username:** @${profile.username}\n`
        markdown += `- **Full Name:** ${profile.full_name}\n`
        if (profile.email) markdown += `- **Email:** ${profile.email}\n`
        if (profile.phone) markdown += `- **Phone:** ${profile.phone}\n`
        if (profile.followers != null)
          markdown += `- **Followers:** ${profile.followers.toLocaleString()}\n`
        if (profile.following != null)
          markdown += `- **Following:** ${profile.following.toLocaleString()}\n`
        if (profile.posts != null) markdown += `- **Posts:** ${profile.posts.toLocaleString()}\n`
        if (profile.bio) markdown += `- **Bio:** ${profile.bio}\n`
        if (profile.website) markdown += `- **Website:** ${profile.website}\n`
        if (profile.location) markdown += `- **Location:** ${profile.location}\n`
        if (profile.category) markdown += `- **Category:** ${profile.category}\n`
        if (profile.is_verified !== undefined)
          markdown += `- **Verified:** ${profile.is_verified ? 'Yes' : 'No'}\n`
        if (profile.is_business !== undefined)
          markdown += `- **Business:** ${profile.is_business ? 'Yes' : 'No'}\n`
        if (profile.is_private !== undefined)
          markdown += `- **Private:** ${profile.is_private ? 'Yes' : 'No'}\n\n`
      }
      break

    case 'scrapeFollowers':
      markdown += `**Action:** Scrape Followers\n**Username:** @${params.username}\n`
      if (params.emailLimit) markdown += `**Email Limit:** ${params.emailLimit}\n`
      markdown += `\n`
      if ('total_scraped' in result) {
        markdown += `**Total Scraped:** ${result.total_scraped}\n\n`
        if ('profiles' in result && result.profiles.length > 0) {
          markdown += `### Follower Profiles\n\n`
          result.profiles.forEach((profile, index) => {
            const followerCount =
              profile.followers != null ? profile.followers.toLocaleString() : '0'
            markdown += `${index + 1}. **@${profile.username}** - ${profile.full_name} (${followerCount} followers)\n`
          })
          markdown += `\n`
        }
      }
      break

    case 'scrapeFollowing':
      markdown += `**Action:** Scrape Following\n**Username:** @${params.username}\n`
      if (params.emailLimit) markdown += `**Email Limit:** ${params.emailLimit}\n`
      markdown += `\n`
      if ('total_scraped' in result) {
        markdown += `**Total Scraped:** ${result.total_scraped}\n\n`
        if ('profiles' in result && result.profiles.length > 0) {
          markdown += `### Following Profiles\n\n`
          result.profiles.forEach((profile, index) => {
            const followerCount =
              profile.followers != null ? profile.followers.toLocaleString() : '0'
            markdown += `${index + 1}. **@${profile.username}** - ${profile.full_name} (${followerCount} followers)\n`
          })
          markdown += `\n`
        }
      }
      break
  }

  markdown += '---\n'
  return markdown
}

export const InstagramScraperDisplay = createWorkflowToolComponent(
  InstagramScraperDisplayComponent,
  downloadInstagramScraper,
  instagramScraperToMarkdown
)
